// AE Header — language dropdown toggle + mobile drawer. Vanilla, no deps.
(() => {
  const header = document.querySelector('ae-header');
  if (!header) return;

  /* ---- sticky offset: measure everything in #header-group ABOVE the header
     (shipping bar) so the group can stick at a negative top — the bar scrolls
     away, the header pins. ---- */
  const group = document.getElementById('header-group');
  const ownSection = header.closest('.shopify-section');
  if (group && ownSection && header.classList.contains('ae-hdr--sticky')) {
    const updateOffset = () => {
      let h = 0;
      for (const child of group.children) {
        if (child === ownSection) break;
        h += child.offsetHeight;
      }
      group.style.setProperty('--ae-prehdr-h', h + 'px');
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    // shipping bar text/timer can change its height after load
    if (window.ResizeObserver && ownSection.previousElementSibling) {
      new ResizeObserver(updateOffset).observe(group);
    }
  }

  /* ---- language dropdown (click to toggle; hover also works via CSS) ---- */
  const lang = header.querySelector('.ae-lang');
  if (lang) {
    const btn = lang.querySelector('.ae-lang__btn');
    const close = () => { lang.removeAttribute('open'); btn?.setAttribute('aria-expanded', 'false'); };
    const open = () => { lang.setAttribute('open', ''); btn?.setAttribute('aria-expanded', 'true'); };
    btn?.addEventListener('click', (e) => {
      e.preventDefault();
      lang.hasAttribute('open') ? close() : open();
    });
    document.addEventListener('click', (e) => { if (!lang.contains(e.target)) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* ---- mobile drawer ---- */
  const drawer = header.querySelector('ae-drawer');
  const burger = header.querySelector('.ae-hdr__burger');
  if (drawer && burger) {
    const openDrawer = () => {
      drawer.hidden = false;
      // next frame so the transform transition runs
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      burger.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
    };
    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      // hide after the transition so it's not focusable
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 320);
    };
    burger.addEventListener('click', openDrawer);
    drawer.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !drawer.hidden) closeDrawer(); });
  }
})();

/* ---- mobile cart ribbon -----------------------------------------------------
   Keeps the under-header cart bar in sync. The theme fires 'cart:update' for
   both adds and quantity changes; state comes from /cart.js (single source of
   truth, works no matter which component changed the cart). ------------------ */
(() => {
  const ribbon = document.querySelector('.ae-cart-ribbon');
  if (!ribbon) return;

  const countEl = ribbon.querySelector('[data-ribbon-count]');
  const totalEl = ribbon.querySelector('[data-ribbon-total]');
  const flashEl = ribbon.querySelector('[data-ribbon-flash]');
  let prevCount = parseInt(ribbon.dataset.itemCount, 10) || 0;
  let flashTimer;
  const tplOne = ribbon.dataset.countOne || '1 item';
  const tplOther = ribbon.dataset.countOther || '2 items';
  const moneyFormat = ribbon.dataset.moneyFormat || '€{{amount}}';

  const formatMoney = (cents) => {
    const withSep = (num, thousands, decimal, decimals) => {
      const fixed = (cents / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withSep(cents, ',', '.', 0);
        case 'amount_with_comma_separator': return withSep(cents, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return withSep(cents, '.', ',', 0);
        case 'amount_with_apostrophe_separator': return withSep(cents, "'", '.', 2);
        default: return withSep(cents, ',', '.', 2); // 'amount'
      }
    });
  };

  let inFlight = null;
  document.addEventListener('cart:update', () => {
    // collapse rapid successive updates into the latest fetch
    const req = (inFlight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        if (req !== inFlight) return;
        if (!cart || typeof cart.item_count !== 'number') return;
        const added = cart.item_count > prevCount;
        prevCount = cart.item_count;
        if (cart.item_count === 0) { ribbon.hidden = true; return; }
        const tpl = cart.item_count === 1 ? tplOne : tplOther;
        countEl.textContent = tpl.replace(/\d+/, String(cart.item_count));
        totalEl.textContent = formatMoney(cart.total_price);
        ribbon.hidden = false;
        ribbon.classList.remove('is-pulse', 'is-bump');
        void ribbon.offsetWidth; // restart the animations
        ribbon.classList.add('is-pulse');
        if (added && flashEl) {
          // "✓ Added" overlays the summary for 2s, then the fresh count/total
          // (already updated underneath) is revealed with a bump.
          clearTimeout(flashTimer);
          flashEl.hidden = true;
          void flashEl.offsetWidth; // restart slide-in if already visible
          flashEl.hidden = false;
          flashTimer = setTimeout(() => {
            flashEl.hidden = true;
            ribbon.classList.add('is-bump');
          }, 2000);
        }
      })
      .catch(() => {}));
  });
})();

/* ---- language suggestion bar ------------------------------------------------
   If the visitor's browser prefers a language we publish that isn't the one
   they're on, offer a one-time, dismissible nudge to switch. Browser language
   (not geo-IP) is the right signal here: it's literally what they read. ------ */
(() => {
  const tip = document.querySelector('ae-lang-tip');
  if (!tip) return;

  const DISMISS_KEY = 'ae-lang-tip-dismissed';
  let data;
  try { data = JSON.parse(tip.querySelector('[data-ae-lang-json]').textContent); } catch { return; }
  if (!data || !Array.isArray(data.languages)) return;

  // UI strings per TARGET language. `msg` is a complete sentence that names the
  // language, written in that language (it's suggesting a switch TO it). `{lang}`
  // is only used by the English fallback for any locale without its own entry.
  const STR = {
    en: { msg: 'This store is also available in {lang}.', sw: 'Switch to {lang}', x: 'Dismiss' },
    nl: { msg: 'Deze winkel is ook beschikbaar in het Nederlands.', sw: 'Overschakelen', x: 'Sluiten' },
    de: { msg: 'Dieser Shop ist auch auf Deutsch verfügbar.', sw: 'Wechseln', x: 'Schließen' },
    es: { msg: 'Esta tienda también está disponible en español.', sw: 'Cambiar', x: 'Cerrar' },
    fr: { msg: 'Cette boutique est aussi disponible en français.', sw: 'Changer', x: 'Fermer' },
    it: { msg: 'Questo negozio è disponibile anche in italiano.', sw: 'Cambia', x: 'Chiudi' },
    pt: { msg: 'Esta loja também está disponível em português.', sw: 'Mudar', x: 'Fechar' },
    el: { msg: 'Αυτό το κατάστημα διατίθεται και στα Ελληνικά.', sw: 'Αλλαγή', x: 'Κλείσιμο' },
    lv: { msg: 'Šis veikals ir pieejams arī latviešu valodā.', sw: 'Pārslēgt', x: 'Aizvērt' },
    hu: { msg: 'Ez a bolt magyarul is elérhető.', sw: 'Váltás', x: 'Bezárás' },
    pl: { msg: 'Ten sklep jest też dostępny po polsku.', sw: 'Przełącz', x: 'Zamknij' },
    nb: { msg: 'Denne butikken er også tilgjengelig på norsk.', sw: 'Bytt', x: 'Lukk' },
    no: { msg: 'Denne butikken er også tilgjengelig på norsk.', sw: 'Bytt', x: 'Lukk' },
    fi: { msg: 'Tämä kauppa on saatavilla myös suomeksi.', sw: 'Vaihda', x: 'Sulje' },
    da: { msg: 'Denne butik er også tilgængelig på dansk.', sw: 'Skift', x: 'Luk' },
    cs: { msg: 'Tento obchod je k dispozici také v češtině.', sw: 'Přepnout', x: 'Zavřít' },
  };
  const primary = (code) => (code || '').toLowerCase().split('-')[0];

  try { if (localStorage.getItem(DISMISS_KEY)) return; } catch {}

  const currentPrimary = primary(data.current);
  const byPrimary = {};
  data.languages.forEach((l) => { const p = primary(l.iso); if (!byPrimary[p]) byPrimary[p] = l; });

  // First browser preference we actually publish wins (respects their ranking).
  const prefs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  let match = null;
  for (const nav of prefs) {
    const hit = byPrimary[primary(nav)];
    if (hit) { match = hit; break; }
  }
  // Nothing to suggest if their top publishable preference is the current language.
  if (!match || primary(match.iso) === currentPrimary) return;

  const strings = STR[primary(match.iso)] || STR.en;
  const fill = (s) => s.replace('{lang}', match.name);
  tip.querySelector('[data-tip-msg]').textContent = fill(strings.msg);
  const switchBtn = tip.querySelector('[data-tip-switch]');
  const dismissBtn = tip.querySelector('[data-tip-dismiss]');
  switchBtn.textContent = fill(strings.sw);
  dismissBtn.setAttribute('aria-label', strings.x);

  tip.hidden = false;
  requestAnimationFrame(() => tip.classList.add('is-in'));

  switchBtn.addEventListener('click', () => {
    // form button values carry the full downcased iso (e.g. "pt-pt")
    const full = match.iso.toLowerCase();
    const btn = tip.querySelector('[data-tip-form-btn="' + (window.CSS && CSS.escape ? CSS.escape(full) : full) + '"]');
    if (btn) btn.click();
  });
  dismissBtn.addEventListener('click', () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    tip.classList.remove('is-in');
    setTimeout(() => { tip.hidden = true; }, 300);
  });
})();
